// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type BaseQueryFn,
  type FetchBaseQueryError,
  type FetchBaseQueryArgs,
  type FetchBaseQueryMeta,
  fetchBaseQuery,
  type FetchArgs,
} from '@reduxjs/toolkit/query';
import { authTokenQuery, type AuthTokenQueryOptions } from './auth-token-query';
import {
  trackPromiseQuery,
  type TrackPromiseQueryOptions,
} from './track-promise-query';
import { currencyQuery, type CurrencyQueryOptions } from './currency-query';
import QueryString from 'qs';
import { selectAccountCurrency } from '@shared/data-access/slices/user/user-selectors';
import { delayQuery, type DelayQueryOptions } from './delay-query';
import { binaryQuery, type BinaryQueryOptions } from './binary-query';
import { multipartQuery, type MultipartQueryOptions } from './multipart-query';

export type BaseQueryExtraOptions = AuthTokenQueryOptions &
  TrackPromiseQueryOptions &
  CurrencyQueryOptions &
  DelayQueryOptions;

export interface FetchQueryArgs extends FetchBaseQueryArgs, BaseQueryExtraOptions {}

export type FetchQueryFn = BaseQueryFn<
  FetchArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions & BinaryQueryOptions & MultipartQueryOptions,
  FetchBaseQueryMeta
>;

const defaultParamsSerializer = (params: Record<string, any>) => {
  return QueryString.stringify(params, { encode: false });
};

export const fetchQuery = ({
  authTokenUsage,
  includeCurrency,
  trackPromiseId,
  useTrackPromise,
  delay,
  paramsSerializer = defaultParamsSerializer,
  ...args
}: FetchQueryArgs): FetchQueryFn => {
  const baseQuery = fetchBaseQuery(args);

  const queryWithDelay = delayQuery(baseQuery, {
    delay,
  });

  const queryWithTrackPromise = trackPromiseQuery(queryWithDelay, {
    useTrackPromise,
    trackPromiseId,
  });

  const queryWithCurrency = currencyQuery(queryWithTrackPromise, {
    includeCurrency,
    selector: selectAccountCurrency,
  });

  const queryWithAuth = authTokenQuery(queryWithCurrency, {
    authTokenUsage,
  });

  const queryWithMultipart = multipartQuery(queryWithAuth);

  const queryWithBinary = binaryQuery(queryWithMultipart);

  return queryWithBinary as FetchQueryFn;
};
